import type { IContentSearchResponse, IResearchSearch } from '@/interfaces';
import type { HttpClient } from '@/services/Http/HttpClient';
import { logger } from '@/utils/logger/logger';
import { searchRequestAsFrontendQuery } from '@/utils/siteUtils';
import type { Filter, Sort } from 'bnef';
import type { ParsedUrlQuery } from 'querystring';

// todo - it would be nice to reconcile this with the enum ContentType
// Analyst Reaction / interactiveDataset needs to be same case
export enum SearchContentType {
    Insight = 'insight',
    Short = 'short',
    AnalystReaction = 'analystreaction',
    Presentation = 'eventsession',
    Theme = 'theme',
    InteractiveDataset = 'interactiveDataset',
    Data = 'data',
    News = 'news',
    Event = 'event',
    Video = 'video',
    GeoEconomics = 'geoEconomics',
    GeoEconomicsNews = 'geoEconomicsNews',
}

const RESEARCH_SEARCH_CONTENT_TYPES = new Set([
    SearchContentType.Insight,
    SearchContentType.Short,
    SearchContentType.AnalystReaction,
    SearchContentType.Presentation,
    // TODO: add themes
]);

export enum Order {
    Relevance = 'relevance',
    Date = 'date',
}

interface ISearchParameters {
    q?: string;
    page?: number;
    order?: Order;
    subscribed?: string;
}

export interface ISearchFilters {
    contentType?: SearchContentType;
    region?: string[];
    sector?: string[];
    author?: string[];
    'video-type'?: string;
    'policy-type'?: string[];
    'policy-status'?: string[];
    'policy-application'?: string[];
    market?: string[];
    pageSize?: number;
    page?: number;
    type?: SearchContentType;
    subtype?: string[];
    sort?: Order;
    _?: string; // placeholder for the end of the URL to help with copy/pasting. No data is stored in it.
}

const DEFAULT_PARAMS: ISearchParameters = {
    q: '',
    page: 1,
    order: Order.Date,
};

export const hasActiveQuery = (query?: ParsedUrlQuery, filters?: Filter[]): boolean => {
    if (!query) {
        return false;
    }
    // known parameters that are outside of filters
    if (query.q || query.sort) {
        return true;
    }
    for (const baseKey of Object.keys(DEFAULT_PARAMS)) {
        if (query[baseKey]) {
            return true;
        }
    }
    if (!filters) {
        return false;
    }
    for (const key in filters) {
        if (query[filters[key]?.param]) {
            return true;
        }
    }
    return false;
};

export const getClientSearchUrl = (path: string, params: ISearchFilters) => {
    return `${path}?${searchRequestAsFrontendQuery(params)}`;
};

export const getSearchQueryObject = (query: ParsedUrlQuery, searchDefaults?: ISearchFilters): ISearchFilters => {
    return {
        ...searchDefaults,
        ...query,
    };
};

const fetchResearchSearch = (api: HttpClient, query: ParsedUrlQuery, searchDefaults: ISearchFilters) =>
    api.search({ path: '/research_search', body: getSearchQueryObject(query, searchDefaults) });

const fetchEventsSearch = (api: HttpClient, query: ParsedUrlQuery, searchDefaults: ISearchFilters) =>
    api.search({ path: '/events_search', body: getSearchQueryObject(query, searchDefaults) });

const fetchContentSearch = (api: HttpClient, query: ParsedUrlQuery, searchDefaults: ISearchFilters) =>
    api.get({ path: '/content_search', query: getSearchQueryObject(query, searchDefaults) });

export const getSearchResults = async (
    api: HttpClient,
    query: ParsedUrlQuery,
    searchDefaults: ISearchFilters
): Promise<IContentSearchResponse<any>> => {
    let results;
    if (searchDefaults?.type && RESEARCH_SEARCH_CONTENT_TYPES.has(searchDefaults.type)) {
        results = await fetchResearchSearch(api, query, searchDefaults);
    } else if (searchDefaults?.contentType === SearchContentType.Event) {
        results = await fetchEventsSearch(api, query, searchDefaults);
    } else {
        results = await fetchContentSearch(api, query, searchDefaults);
    }

    return results || { items: [] };
};

export const SearchContentDefaults = {
    Insights: {
        type: SearchContentType.Insight,
        sort: Order.Date,
    } as ISearchFilters,
    Shorts: {
        type: SearchContentType.Short,
        sort: Order.Date,
    } as ISearchFilters,
    AnalystReactions: {
        type: SearchContentType.AnalystReaction,
        sort: Order.Date,
    } as ISearchFilters,
    InteractiveDatasets: {
        contentType: SearchContentType.InteractiveDataset,
    } as ISearchFilters,
    MarketOutlooks: {
        type: SearchContentType.Insight,
        sort: Order.Date,
        subtype: ['market-outlook'],
    } as ISearchFilters,
    Videos: {
        contentType: SearchContentType.Video,
    } as ISearchFilters,
    Themes: {
        contentType: SearchContentType.Theme,
        // only one page of themes can be supported
        pageSize: 500,
    } as ISearchFilters,
    News: {
        contentType: SearchContentType.News,
    } as ISearchFilters,
    Data: {
        contentType: SearchContentType.Data,
    } as ISearchFilters,
    Events: {
        contentType: SearchContentType.Event,
        sort: Order.Relevance,
    } as ISearchFilters,
    PastEvents: {
        contentType: SearchContentType.Event,
        sort: Order.Date,
    } as ISearchFilters,
    GeoEconomics: {
        contentType: SearchContentType.GeoEconomics,
        pageSize: 30,
    } as ISearchFilters,
    GeoEconomicsNews: {
        contentType: SearchContentType.GeoEconomicsNews,
        pageSize: 30,
    } as ISearchFilters,
} as const;

const SORT_PARAM_SEPARATOR = '.';

/** @deprecated */
export const parseSortParam = (input?: string | string[]): Sort | null => {
    if (!input || typeof input !== 'string') {
        return null;
    }
    const parts = input.toString().split(SORT_PARAM_SEPARATOR);
    let direction = parts.pop();

    if (!['asc', 'desc'].includes(direction)) {
        // must not be a direction, put it back
        direction = undefined;
        parts.push(direction);
    }
    return {
        name: parts.join(SORT_PARAM_SEPARATOR), // to support deep object search
        direction: direction as Sort['direction'] | undefined,
    };
};

export const getAllResearchQuery = (type: SearchContentType, queryOverride?: Record<string, any>): IResearchSearch => {
    return {
        q: '',
        type,
        page: 1,
        sort: !queryOverride ? 'date' : undefined,
        ...queryOverride,
    };
};

/**
 * Get the smart search destination URL.
 * Queries the backend to determine the best search page and filters for the given query.
 * Returns a full URL (path + query params) ready to redirect to.
 * If the endpoint fails or query is missing, returns '/search' with the query parameter preserved.
 */
export const getSmartSearchUrl = async (api: HttpClient, query: ParsedUrlQuery): Promise<string> => {
    const q = typeof query.q === 'string' ? query.q : undefined;

    if (!q) {
        return '/search';
    }

    try {
        const response = await api.get({
            path: '/smart_search',
            query: { q },
        });

        if (response?.url && typeof response.url === 'string') {
            return response.url;
        }
    } catch (error) {
        // If endpoint fails, safely fall back to /search with query preserved
        logger.warn('Failed to fetch smart search URL', { error, query: q });
    }

    return `/search?q=${encodeURIComponent(q)}`;
};
